/**
 * KBC Event Banner Component
 * Displays a prominent banner on the landing page for the quiz event
 * 
 * TEMPORARY - FOR EVENT ONLY
 * This file can be safely deleted when removing the quiz feature
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { motion } from 'framer-motion';
import { Trophy, Sparkles, ArrowRight } from 'lucide-react';
import { loginRequest } from '../../authConfig';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { safeLoginRedirect } from '../../utils/msalHelpers';

interface KBCEventBannerProps {
  isAuthenticated?: boolean;
}

export default function KBCEventBanner({ isAuthenticated = false }: KBCEventBannerProps) {
  const navigate = useNavigate();
  const { instance, inProgress } = useMsal();

  const handlePlayQuiz = async () => {
    if (!FEATURE_FLAGS.REQUIRE_AUTHENTICATION) {
      navigate('/kbc-quiz');
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      console.log('Authentication interaction already in progress, skipping...', inProgress);
      return;
    }

    if (isAuthenticated) {
      // Set flag to indicate user clicked to go to quiz
      sessionStorage.setItem('userClickedQuiz', 'true');
      navigate('/kbc-quiz');
    } else {
      try {
        console.log('Initiating login for quiz...');
        // Set flag to indicate user intended to login and go to quiz
        sessionStorage.setItem('userClickedQuiz', 'true');
        await safeLoginRedirect(instance, loginRequest);
      } catch (error: any) {
        console.error("Login error:", error);
        if (error?.errorCode !== 'interaction_in_progress') {
          alert(`Login failed: ${error?.message || 'Unknown error'}. Please try again.`);
        }
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="relative w-full max-w-7xl mx-auto px-2 sm:px-4"
    >
      {/* Banner Container */}
      <div className="relative overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br from-kbc-dark via-purple-900 to-kbc-dark border-2 border-kbc-gold shadow-2xl">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,215,0,0.3),transparent_50%)]" />
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute top-0 right-0 w-96 h-96 bg-kbc-gold rounded-full blur-3xl"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 px-4 py-4 sm:px-6 sm:py-5 md:px-12 md:py-8 flex flex-col md:flex-row items-center md:justify-between gap-4 md:gap-6">
          {/* Left Side - Icon and Text */}
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-1 w-full md:w-auto">
            {/* Trophy Icon with Animation - Hidden on mobile */}
            <motion.div
              animate={{
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="hidden md:block flex-shrink-0"
            >
              <div className="relative">
                <Trophy className="w-16 h-16 text-kbc-gold" strokeWidth={2} />
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full bg-kbc-gold blur-xl"
                />
              </div>
            </motion.div>

            {/* Trophy Icon - Mobile version (smaller) */}
            <motion.div
              animate={{
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="block md:hidden flex-shrink-0"
            >
              <div className="relative">
                <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-kbc-gold" strokeWidth={2} />
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full bg-kbc-gold blur-lg"
                />
              </div>
            </motion.div>

            {/* Text Content */}
            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-kbc-amber animate-pulse flex-shrink-0" />
                <span className="text-kbc-amber font-semibold text-xs sm:text-xs uppercase tracking-wider whitespace-nowrap">
                  Special Event
                </span>
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-kbc-amber animate-pulse flex-shrink-0" />
              </div>
              <h2 className="text-lg sm:text-xl md:text-3xl font-bold text-white mb-0.5 sm:mb-1 leading-tight">
                Win Big with the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-kbc-gold via-kbc-amber to-kbc-gold">
                  KBC Quiz Challenge
                </span>
              </h2>
              <p className="text-gray-300 text-xs sm:text-sm md:text-lg leading-snug hidden sm:block">
                Test your knowledge and compete for prizes!
              </p>
            </div>
          </div>

          {/* Right Side - CTA Button */}
          <motion.button
            onClick={handlePlayQuiz}
            disabled={inProgress !== InteractionStatus.None}
            whileHover={{ scale: inProgress === InteractionStatus.None ? 1.05 : 1 }}
            whileTap={{ scale: inProgress === InteractionStatus.None ? 0.95 : 1 }}
            className={`
              relative group w-full md:w-auto
              px-4 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 
              rounded-lg sm:rounded-xl 
              font-bold text-sm sm:text-base md:text-lg
              flex items-center justify-center gap-2 md:gap-3 
              transition-all duration-300 whitespace-nowrap
              bg-gradient-to-r from-kbc-gold to-kbc-amber text-kbc-dark 
              shadow-lg shadow-kbc-gold/50 hover:shadow-2xl hover:shadow-kbc-gold/70
              ${inProgress !== InteractionStatus.None ? 'opacity-60 cursor-not-allowed' : ''}
            `}
          >
            {/* Button Shine Effect */}
            {inProgress === InteractionStatus.None && (
              <motion.div
                animate={{
                  x: ['0', '200%', '0%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  repeatDelay: 0,
                }}
                className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12"
                style={{ pointerEvents: 'none' }}
              />
            )}

            <span className="relative z-10">
              {inProgress !== InteractionStatus.None ? 'Loading...' : 
               !FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? 'Play Quiz Now' :
               isAuthenticated ? 'Play Quiz Now' : 'Login to Play'}
            </span>
            <ArrowRight
              className={`relative z-10 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transition-transform duration-300 flex-shrink-0 ${
                inProgress === InteractionStatus.None ? 'group-hover:translate-x-1' : ''
              }`}
            />
          </motion.button>
        </div>

        {/* Bottom Decorative Border */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-kbc-gold to-transparent" />
      </div>

      {/* Optional: Event Details Below Banner */}
      {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-400"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-kbc-success animate-pulse" />
          <span>20 Questions</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-kbc-success animate-pulse" />
          <span>3 Attempts Per Person</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-kbc-success animate-pulse" />
          <span>Real-time Leaderboard</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-kbc-success animate-pulse" />
          <span>Exciting Prizes</span>
        </div>
      </motion.div> */}
    </motion.div>
  );
}
